import React, { useEffect, useState } from "react";
import { Icon } from "@blueprintjs/core";
import TVHeroImg from "./assets/TVHero.png";
import TVAImg from "./assets/TVA.png";
import TVBImg from "./assets/TVB.png";
import TVBAplusImg from "./assets/TVB_Aplus.png";
import EStarImg from "./assets/EStar.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";
import { nudge } from "../../../../translations/experiments/Nudge";

type TVScreenType = {
    title: string;
    description: string;
    optionA: string;
    optionB: string;
    price: string;
    data: { label: string; value: string }[];
};

export type TVChoice = 0 | 1;

export interface SelectionData {
    choice: number;
    millisecondsSinceFirstRender: number;
}

export interface TVScreenResult {
    selections: SelectionData[];
}

export const TVScreen3InitialResult: TVScreenResult = {
    selections: []
};

function ProductDataTable({ data }: { data: { label: string; value: string }[] }) {
    return (
        <table style={{
            width: "100%",
            borderCollapse: "collapse",
            margin: "16px 0 10px 0",
            background: "#f8f9fa",
            borderRadius: 8,
            overflow: "hidden"
        }}>
            <tbody>
            {data.map(({ label, value }) => (
                <tr key={label} style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <td style={{
                        padding: "6px 10px",
                        fontWeight: 500,
                        color: "#456",
                        textAlign: "left",
                        minWidth: 140
                    }}>{label}</td>
                    <td style={{
                        padding: "6px 10px",
                        color: "#234",
                        textAlign: "right"
                    }}>{value}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

function TVOptionCard({
                          image,
                          overlayImage,
                          title,
                          selected,
                          onClick,
                          price,
                          data
                      }: {
    image: string,
    overlayImage?: string,
    title: string,
    selected: boolean,
    onClick: () => void,
    price: string,
    data: { label: string; value: string }[]
}) {
    // Determine overlay width: EStar logo is 30% smaller
    const overlayWidth = overlayImage && overlayImage === EStarImg ? 95 : 135;

    return (
        <div
            style={selected ? commonStyles.optionCard.selected : commonStyles.optionCard.default}
            onClick={onClick}
        >
            {selected && (
                <div style={commonStyles.selectionCheck}>
                    <Icon icon="tick" size={16} />
                </div>
            )}

            <div style={{
                marginBottom: 14,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                width: "100%",
                textAlign: "center",
                background: "#f8f9fa",
                position: "relative"
            }}>
                <img
                    src={image}
                    alt={title}
                    style={{
                        width: "100%",
                        maxWidth: 270,
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                        margin: "0 auto",
                        padding: "18px 0"
                    }}
                />
                {/* Overlay image in bottom right if provided */}
                {overlayImage && (
                    <img
                        src={overlayImage}
                        alt="Overlay"
                        style={{
                            position: "absolute",
                            right: 10,
                            bottom: 10,
                            width: overlayWidth,
                            height: "auto",
                            zIndex: 2,
                            pointerEvents: "none"
                        }}
                    />
                )}
            </div>
            <h3 style={{
                color: colors.brillantBlue,
                marginBottom: 8,
                fontSize: 18,
                fontWeight: 600,
                textAlign: "center"
            }}>
                {title}
            </h3>
            <div style={{
                color: "#4b4b4b",
                fontWeight: 600,
                fontSize: 17,
                margin: "10px 0 8px 0",
                textAlign: "center"
            }}>
                {price}
            </div>
            <ProductDataTable data={data} />
        </div>
    );
}

export function NudgingScreen3(props: ScreenProps) {
    const { language } = useLanguage();
    const translation = nudge[language]?.screens?.tvGood as TVScreenType;
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);
    const { setCanContinue, setResult } = props;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(false);
        setResult(() => ({ ...TVScreen3InitialResult }));
    }, [setCanContinue, setResult]);

    useEffect(() => {
        setCanContinue(selected !== null);
        if (selected !== null) {
            setResult((prev: TVScreenResult) => {
                return {
                    ...prev,
                    selections: [
                        {
                            choice: selected,
                            millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                        }
                    ]
                };
            });
        }
    }, [selected, setCanContinue, setResult, firstRenderTimestamp]);

    const chooseQuestion = translation.title;

    // Add Stromverbrauch/Power consumption label if not present
    const tvAData = translation.data.some(d => (d.label === "Stromverbrauch" || d.label === "Power consumption"))
        ? translation.data
        : [...translation.data, { label: language === "de" ? "Stromverbrauch" : "Power consumption", value: "95 Watt" }];

    const tvBData = tvAData; // Same data for both options in this screen

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28,
                textAlign: "center"
            }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {translation.title}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word",
                    marginBottom: 24
                }}>
                    {translation.description}
                </div>
                <div style={{
                    width: "100%",
                    maxWidth: 540,
                    margin: "0 auto 8px auto",
                    background: "#f8f9fa",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 6px 20px 0 rgba(0,0,0,0.08)"
                }}>
                    <img
                        src={TVHeroImg}
                        alt="TV Auswahl"
                        style={{
                            width: "100%",
                            height: "auto",
                            display: "block"
                        }}
                    />
                </div>
            </div>

            <div style={{ textAlign: "center", margin: "0 0 28px 0" }}>
                <span style={{
                    color: colors.brillantBlue,
                    fontSize: 22,
                    fontWeight: 600,
                    textAlign: "center"
                }}>{chooseQuestion}</span>
            </div>

            <div style={{
                display: "flex",
                flexDirection: window.innerWidth < 600 ? "column" : "row",
                gap: 24,
                justifyContent: "center",
                alignItems: "flex-start"
            }}>
                <TVOptionCard
                    image={TVAImg}
                    overlayImage={EStarImg}
                    title={translation.optionA}
                    selected={selected === 0}
                    onClick={() => setSelected(0)}
                    price={translation.price}
                    data={tvAData}
                />
                <TVOptionCard
                    image={TVBImg}
                    overlayImage={TVBAplusImg}
                    title={translation.optionB}
                    selected={selected === 1}
                    onClick={() => setSelected(1)}
                    price={translation.price}
                    data={tvBData}
                />
            </div>

            {selected !== null && (
                <div style={{
                    textAlign: "center",
                    marginTop: 28,
                    padding: "12px 20px",
                    fontSize: 18,
                    fontWeight: 500,
                    color: colors.brillantBlue,
                    backgroundColor: colors.lichtBlauLighter,
                    borderRadius: 8,
                    display: "inline-block",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}>
                    <Icon icon="tick-circle" style={{ marginRight: 8 }} />
                    {selected === 0
                        ? translation.optionA
                        : translation.optionB
                    }
                </div>
            )}
        </div>
    );
}

export const NudgingScreen3Config: ScreenConfig = {
    component: NudgingScreen3,
    defaultResult: TVScreen3InitialResult,
    canContinueDefault: false
};